import type { ReactNode } from 'react'
import { useEffect } from 'react'
import { useNavigate } from 'react-router'
import { useOrgGroupAccess } from '@/hooks/useOrgGroupAccess'
import { LoadingSpinner } from '@/components/layout/LoadingSpinner'
import type { ClerkUserRole } from '@/types/auth'
import type { FeatureAccessPath } from '@/types/configuration'

type OrgAccessGuardProps = {
  configPath: FeatureAccessPath
  restrictedRoles?: ClerkUserRole[]
  children: ReactNode
  fallback?: ReactNode
}

export function OrgAccessGuard({ configPath, restrictedRoles, children, fallback }: OrgAccessGuardProps) {
  const { hasAccess, isLoading } = useOrgGroupAccess({
    configPath,
    restrictedRoles,
  })
  const navigate = useNavigate()

  useEffect(() => {
    // Only redirect if not loading and no access
    if (!isLoading && !hasAccess && !fallback) {
      navigate('/')
    }
  }, [isLoading, hasAccess, fallback, navigate])

  if (isLoading) return <LoadingSpinner />

  if (!hasAccess) {
    if (fallback) return <>{fallback}</>
    return null // Will redirect via useEffect
  }

  return <>{children}</>
}
